'use client';

import { useCallback, useRef, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';

import { Icons } from '@/Icons';
import { CopySvg } from '@/Icons/Svgs/CopySvg';
import { CheckSvg } from '@/Icons/Svgs/CheckSvg';
import { CrossSvg } from '@/Icons/Svgs/CrossSvg';
import theme from '@/theme';
import {
  CheckConfig,
  CheckResult,
  CheckStatus,
  generateITAdminMessage,
  getChecks,
  getSmartHints,
  runChecks,
} from '@/lib/diagnostic-checks';

type DiagnosticState = 'ready' | 'running' | 'done';

export default function DiagnosePage() {
  const [state, setState] = useState<DiagnosticState>('ready');
  const [results, setResults] = useState<Map<string, CheckResult>>(new Map());
  const [copied, setCopied] = useState(false);
  const checksRef = useRef<CheckConfig[]>([]);

  const handleRun = useCallback(async () => {
    const checks = getChecks();
    checksRef.current = checks;

    const initial = new Map<string, CheckResult>();
    for (const c of checks) {
      initial.set(c.id, { id: c.id, status: 'pending' });
    }
    setResults(initial);
    setState('running');

    const onUpdate = (id: string, partial: Partial<CheckResult>) => {
      setResults((prev) => {
        const next = new Map(prev);
        const existing = next.get(id) ?? {
          id,
          status: 'pending' as CheckStatus,
        };
        next.set(id, { ...existing, ...partial });
        return next;
      });
    };

    await runChecks(checks, onUpdate);
    setState('done');
  }, []);

  const handleCopy = useCallback(() => {
    const checks = checksRef.current;
    const allResults = Array.from(results.values());
    const text = generateITAdminMessage(allResults, checks);
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [results]);

  const allResults = Array.from(results.values());
  const allPassed =
    state === 'done' && allResults.every((r) => r.status === 'pass');
  const hasFailed =
    state === 'done' && allResults.some((r) => r.status === 'fail');
  const hints = state === 'done' ? getSmartHints(allResults) : [];

  return (
    <PageContainer>
      <ContentCard>
        <LogoWrapper>
          <Icons.Logo />
        </LogoWrapper>

        {state === 'ready' && (
          <>
            <Title>Will Curipod work in your classroom?</Title>
            <Description>
              Tap the button and we'll check if your school's network will let
              Curipod through. Takes about 5 seconds — no IT knowledge needed!
            </Description>
            <ButtonRow>
              <PrimaryButton onClick={handleRun}>
                Check My Network
              </PrimaryButton>
            </ButtonRow>
            <NetworkTip>
              💡 For best results, run this check on the school network — once
              on your teacher device and once on a student device.
            </NetworkTip>
          </>
        )}

        {(state === 'running' || state === 'done') && (
          <>
            {state === 'running' && (
              <RunningHeading>Checking your network…</RunningHeading>
            )}

            {allPassed && (
              <ResultHeading $variant="success">
                You're all set! 🎉
              </ResultHeading>
            )}

            {hasFailed && (
              <ResultHeading $variant="error">
                A few things are blocked
              </ResultHeading>
            )}

            <CheckList>
              {checksRef.current.map((check) => {
                const result = results.get(check.id);
                return (
                  <CheckRow
                    key={check.id}
                    $status={result?.status ?? 'pending'}>
                    <StatusCell>
                      <StatusIndicator status={result?.status ?? 'pending'} />
                    </StatusCell>
                    <CheckInfo>
                      <CheckLabel>{check.label}</CheckLabel>
                      {result?.status === 'fail' && (
                        <CheckDomain>{check.domain}</CheckDomain>
                      )}
                    </CheckInfo>
                    {result?.durationMs != null && (
                      <Duration>{result.durationMs}ms</Duration>
                    )}
                  </CheckRow>
                );
              })}
            </CheckList>

            {allPassed && (
              <Banner $variant="success">
                Curipod will work great in your classroom. You're good to go!
              </Banner>
            )}

            {hasFailed && (
              <>
                <Banner $variant="error">
                  Don't worry — your IT team can fix this quickly. Use the
                  button below to send them exactly what they need to unblock
                  Curipod.
                </Banner>

                {hints.length > 0 && (
                  <HintList>
                    {hints.map((hint, i) => (
                      <Hint key={i}>{hint}</Hint>
                    ))}
                  </HintList>
                )}

                <ButtonRow>
                  <GhostButton onClick={handleCopy}>
                    <IconWrap>{copied ? <CheckSvg /> : <CopySvg />}</IconWrap>
                    {copied ? 'Copied!' : 'Copy for IT Admin'}
                  </GhostButton>
                </ButtonRow>
              </>
            )}

            {state === 'done' && (
              <ButtonRow>
                <TertiaryButton onClick={handleRun}>Check Again</TertiaryButton>
              </ButtonRow>
            )}
          </>
        )}
      </ContentCard>
    </PageContainer>
  );
}

function StatusIndicator({ status }: { status: CheckStatus }) {
  switch (status) {
    case 'pending':
      return <PendingDot />;
    case 'running':
      return <SpinnerRing />;
    case 'pass':
      return (
        <PassIconWrap>
          <CheckSvg />
        </PassIconWrap>
      );
    case 'fail':
      return (
        <FailIconWrap>
          <CrossSvg />
        </FailIconWrap>
      );
  }
}

// Design tokens from design.md
const SETTINGS_BG = 'rgb(251, 247, 242)';
const EASE = 'all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1)';

// Layout

const PageContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  height: 100%;
  overflow-y: auto;
  background: ${SETTINGS_BG};
  padding: 32px 16px;
  box-sizing: border-box;
`;

const ContentCard = styled.div`
  background: #fff;
  border-radius: 12px;
  box-shadow: ${theme.effects.barelyFloating};
  max-width: 500px;
  width: 100%;
  padding: 40px 32px;
  display: flex;
  flex-direction: column;
  align-items: center;
  transition: ${EASE};
`;

const LogoWrapper = styled.div`
  margin-bottom: 24px;

  svg {
    width: 110px;
    height: auto;
  }
`;

// SectionTitle from design.md
const Title = styled.h1`
  font-family: 'Nunito', sans-serif;
  font-size: 18px;
  font-weight: 700;
  color: ${theme.colors.black};
  margin: 0 0 12px;
  text-align: center;
`;

// SectionDescription from design.md
const Description = styled.p`
  font-family: 'Nunito', sans-serif;
  font-size: 14px;
  color: ${theme.colors.greyDark};
  text-align: center;
  line-height: 1.6;
  margin: 0 0 24px;
`;

const NetworkTip = styled.p`
  font-family: 'Nunito', sans-serif;
  font-size: 13px;
  color: ${theme.colors.greyDark};
  text-align: center;
  line-height: 1.5;
  margin: 28px 0 0;
  padding: 10px 14px;
  background: rgba(255, 107, 43, 0.08);
  border-radius: 5px;
  width: 100%;
  box-sizing: border-box;
`;

const RunningHeading = styled.p`
  font-family: 'Nunito', sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: ${theme.colors.greyDark};
  margin: 0 0 16px;
  text-align: center;
`;

const ResultHeading = styled.h2<{ $variant: 'success' | 'error' }>`
  font-family: 'Nunito', sans-serif;
  font-size: 18px;
  font-weight: 700;
  margin: 0 0 16px;
  text-align: center;

  ${(p) =>
    p.$variant === 'success' &&
    css`
      color: ${theme.colors.green};
    `}

  ${(p) =>
    p.$variant === 'error' &&
    css`
      color: ${theme.colors.red};
    `}
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-top: 16px;
  flex-wrap: wrap;
`;

// Orange primary — design.md: orange is the accent theme for utility UIs
const PrimaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: none;
  border-radius: 5px;
  font-family: 'Nunito', sans-serif;
  font-size: 16px;
  font-weight: 700;
  padding: 12px 28px;
  cursor: pointer;
  background: ${theme.colors.orange};
  color: #fff;
  transition: ${EASE};

  &:hover {
    background: #e8853a;
  }

  &:active {
    background: #c96e28;
  }
`;

// Ghost button — design.md spec exactly
const GhostButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  border: 1px solid ${theme.colors.divider};
  border-radius: 5px;
  background: transparent;
  color: ${theme.colors.black};
  font-size: 14px;
  font-weight: 700;
  font-family: 'Nunito', sans-serif;
  cursor: pointer;
  transition: ${EASE};

  &:hover,
  &:active,
  &:focus {
    background: rgba(255, 161, 84, 0.15);
    border-color: ${theme.colors.orange};
    color: ${theme.colors.orange};
  }
`;

const TertiaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid ${theme.colors.divider};
  border-radius: 5px;
  font-family: 'Nunito', sans-serif;
  font-size: 14px;
  font-weight: 700;
  padding: 10px 18px;
  cursor: pointer;
  background: transparent;
  color: ${theme.colors.black};
  transition: ${EASE};

  &:hover {
    border-color: ${theme.colors.orange};
    color: ${theme.colors.orange};
    background: rgba(255, 161, 84, 0.08);
  }
`;

// Check list — InputShell pattern from design.md
const CheckList = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 4px;
`;

const CheckRow = styled.div<{ $status: CheckStatus }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  background: #fff;
  border-radius: 5px;
  box-shadow: ${theme.effects.barelyFloating};
  transition: ${EASE};
  border-left: 3px solid transparent;

  ${(p) =>
    p.$status === 'pass' &&
    css`
      border-left-color: ${theme.colors.green};
    `}

  ${(p) =>
    p.$status === 'fail' &&
    css`
      border-left-color: ${theme.colors.red};
    `}
`;

const StatusCell = styled.div`
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const CheckInfo = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
`;

const CheckLabel = styled.span`
  font-family: 'Nunito', sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: ${theme.colors.black};
`;

const CheckDomain = styled.span`
  font-family: 'Nunito', sans-serif;
  font-size: 12px;
  color: ${theme.colors.text.grey};
  margin-top: 2px;
`;

const Duration = styled.span`
  font-family: 'Nunito', sans-serif;
  font-size: 12px;
  color: ${theme.colors.text.grey};
  flex-shrink: 0;
`;

// Status icons

const PendingDot = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${theme.colors.divider};
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const SpinnerRing = styled.div`
  width: 18px;
  height: 18px;
  border: 2px solid #f3f3f3;
  border-top-color: ${theme.colors.orange};
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;

const PassIconWrap = styled.span`
  display: flex;
  align-items: center;
  color: ${theme.colors.green};

  svg {
    width: 13px;
    height: 13px;
  }
`;

const FailIconWrap = styled.span`
  display: flex;
  align-items: center;
  color: ${theme.colors.red};

  svg {
    width: 13px;
    height: 13px;
  }
`;

// currentColor so icon inherits hover colour — design.md: "Use currentColor for icons"
const IconWrap = styled.span`
  display: flex;
  align-items: center;
  color: currentColor;

  svg {
    width: 14px;
    height: 14px;
  }
`;

// Banners

const Banner = styled.div<{ $variant: 'success' | 'error' }>`
  width: 100%;
  text-align: center;
  font-family: 'Nunito', sans-serif;
  font-size: 14px;
  line-height: 1.5;
  padding: 14px 16px;
  border-radius: 5px;
  margin-top: 4px;
  box-sizing: border-box;

  ${(p) =>
    p.$variant === 'success' &&
    css`
      background: rgba(19, 153, 130, 0.1);
      color: ${theme.colors.green};
      font-weight: 700;
    `}

  ${(p) =>
    p.$variant === 'error' &&
    css`
      background: rgba(216, 76, 66, 0.06);
      color: ${theme.colors.text.dark};
    `}
`;

// Hints — orange accent, PreviewBadge palette from design.md

const HintList = styled.div`
  width: 100%;
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Hint = styled.div`
  font-family: 'Nunito', sans-serif;
  font-size: 13px;
  color: ${theme.colors.black};
  background: rgba(255, 107, 43, 0.08);
  border-left: 3px solid ${theme.colors.orange};
  padding: 10px 14px;
  border-radius: 5px;
  line-height: 1.5;
`;
